import CartItems from "../CartItems/CartItems"
import PaddleCheckout from "../PaddleCheckout/PaddleCheckout"
import { useState, useEffect, useRef } from "react"
import Button from "../Button"

import { useNavigate, useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { fetchCart, syncLocalCartToServer } from "../../store/slices/cartSlice"
import { createOrder } from "../../store/slices/orderSlice"
import { setAuthRedirectPath } from "../../store/userSlice"

import styles from "./CartPopUp.module.css"

const CartPopUp = ({ closeCart }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [orderWarning, setOrderWarning] = useState(null);
  // step: 'cart' — список товаров, 'payment' — Paddle inline checkout
  const [step, setStep] = useState('cart');
  const [orderId, setOrderId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, loading, localItems } = useSelector((state) => state.cart);
  const { isAuthenticated } = useSelector((state) => state.user);

  // Объединяем серверные и локальные товары для отображения
  const allItems = [...(cart?.items || []), ...(localItems || [])];

  // Рассчитываем тотал с учётом локальных товаров
  const localSubtotal = (localItems || []).reduce((sum, item) => sum + (Number(item.price) || 0), 0);
  const localDiscountedTotal = (localItems || []).reduce((sum, item) => sum + (Number(item.discounted_price) || Number(item.price) || 0), 0);
  const combinedSubtotal = (Number(cart?.total_price) || 0) + localSubtotal;
  const combinedTotal = (Number(cart?.total_discounted_price) || Number(cart?.total_price) || 0) + localDiscountedTotal;
  
  const dispatch = useDispatch();
  const popUpRef = useRef(null);

  // Обновляем корзину при монтировании (только для авторизованных)
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchCart());
    }
  }, [dispatch, isAuthenticated]);


  const handleCheckOut = async () => {
    if (!agreedToTerms) {
      return;
    }
    // Если не авторизован — редирект на логин
    if (!isAuthenticated) {
      dispatch(setAuthRedirectPath(location.pathname + location.search));
      handleClose();
      navigate("/authorization");
      return;
    }
    try {
      setIsCreatingOrder(true);
      setOrderError(null);
      setOrderWarning(null);
      // Если есть локальные товары — сначала синхронизируем с сервером
      if (localItems?.length > 0) {
        const syncResult = await dispatch(syncLocalCartToServer()).unwrap();
        const hasUnsyncedItems = Array.isArray(syncResult?.remainingLocalItems) && syncResult.remainingLocalItems.length > 0;
        const hasCheckoutableServerCart = Number(syncResult?.serverCartItemCount || 0) > 0;
        if (!hasCheckoutableServerCart && hasUnsyncedItems) {
          setOrderError("No guest cart items could be synced. Review your cart and try again.");
          return;
        }
        if (hasUnsyncedItems) {
          setOrderWarning("Some cart items could not be synced and remain in your guest cart.");
        }
      }
      const order = await dispatch(createOrder()).unwrap();
      if (order && (Number(order.final_amount) === 0 || order.final_amount === 0 || order.final_amount === "0" || order.final_amount === "0.00")) {
        // Бесплатный заказ — обновляем корзину
        dispatch(fetchCart());
        navigate("/profile");
        handleClose();
      } else {
        // Платный заказ — показываем Paddle прямо в попапе
        setOrderId(order.id);
        setStep('payment');
      }
    } catch (error) {
      console.error("Failed to create order:", error);
      const fatalSyncCode = error?.fatalError?.code;
      const hasUnsyncedItems = Array.isArray(error?.remainingLocalItems) && error.remainingLocalItems.length > 0;
      setOrderError(
        hasUnsyncedItems || fatalSyncCode
          ? "Some cart items could not be synced. Review your cart and try again."
          : "Something went wrong, please try again"
      );
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const handleBackToCart = () => {
    setStep('cart');
    setOrderId(null);
  };

  const handleClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    setIsVisible(false);
  };

  useEffect(() => {
    if (!isVisible && isClosing) {
      const timer = setTimeout(() => {
        closeCart();
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isVisible, isClosing, closeCart]);

  // Закрытие по клику снаружи — только на шаге cart (не на payment, чтобы не потерять оплату)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (step === 'payment') return;
      if (popUpRef.current && !popUpRef.current.contains(event.target)) {
        handleClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isClosing, step]);

  if (loading) {
    return (
      <div className={styles.fixWrap}>
        <div className={styles.main} ref={popUpRef}>
          <div className={styles.navigation}>
            <h3 className="h3-medium">Cart</h3>
            <div className={styles.closeBtn}>
              <Button
                variant="transparent"
                iconType="crossbtn"
                onClick={handleClose}
              />
            </div>
          </div>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            Loading cart...
          </div>
        </div>
      </div>
    );
  }

  if (!cart.items && (!localItems || localItems.length === 0)) {
    return null;
  }

  return (
    <div className={styles.fixWrap}>
      <div
        className={`${styles.main} ${step === 'payment' ? styles.mainPayment : ''} ${!isVisible ? styles.exit : ""}`}
        ref={popUpRef}
      >
        <div className={styles.navigation}>
          <h3 className="h3-medium">{step === 'payment' ? 'Payment' : 'Cart'}</h3>
          <div className={styles.closeBtn}>
            <Button
              variant="transparent"
              iconType="crossbtn"
              onClick={handleClose}
            />
          </div>
        </div>

        {step === 'cart' && (
          <>
            <CartItems cart={allItems} />
            <div className={styles.checkoutField}>
              {allItems.length > 0 && (
                <>
                  <div className={styles.checkInfo}>
                    <span>Subtotal</span>
                    <span>${combinedSubtotal}</span>
                  </div>
                  {cart.total_discount_amount > 0 && (
                    <div className={styles.checkInfo}>
                      <span>Discount ({cart.user_discount_percentage}%)</span>
                      <span>-${cart.total_discount_amount}</span>
                    </div>
                  )}
                  <div className={`${styles.checkInfo} ${styles.totalRow}`}>
                    <h4 className="h4-medium">Total</h4>
                    <h4 className="h4-medium">${combinedTotal || combinedSubtotal}</h4>
                  </div>
                  <div className={styles.agreementBlock}>
                    <label className={styles.agreementLabel}>
                      <input
                        type="checkbox"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        className={styles.agreementCheckbox}
                      />
                      <span>
                        I have read and agree to the{" "}
                        <a href="/terms-of-use" target="_blank" rel="noreferrer" className={styles.agreementLink}>
                          Terms of Use
                        </a>{" "}
                        &{" "}
                        <a href="/privacy-policy" target="_blank" rel="noreferrer" className={styles.agreementLink}>
                          Privacy Policy
                        </a>
                      </span>
                    </label>
                  </div>
                  {orderWarning && (
                    <p style={{ color: '#8a6d1f', fontSize: '13px', margin: '8px 0' }}>{orderWarning}</p>
                  )}
                  {orderError && (
                    <p style={{ color: 'red', fontSize: '13px', margin: '8px 0' }}>{orderError}</p>
                  )}
                  <Button
                    label={isCreatingOrder ? "Processing..." : (combinedTotal === 0 && combinedSubtotal === 0 ? "Complete Free Order" : "Checkout")}
                    variant={agreedToTerms && !isCreatingOrder ? "blue" : "grey"}
                    iconType="arrowRight"
                    onClick={handleCheckOut}
                    disabled={isCreatingOrder || !agreedToTerms}
                  />
                </>
              )}
            </div>
          </>
        )}

        {step === 'payment' && orderId && (
          <div className={styles.paymentContainer}>
            <PaddleCheckout orderId={orderId} orderData={{}} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPopUp;
